import { useState } from 'react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Plus, Save, Calendar, Eye, Edit } from 'lucide-react';
import { scheduleService } from '../../../src/services/schedule.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { Badge } from '../../ui/badge';
import { useAuth } from '../../../src/hooks/useAuth';
import { useProjects } from '../../../src/hooks/queries/use-projects';

// New Schedule Modal Component
export function NewScheduleModal({ onSuccess }: { onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { orgId } = useAuth();
  const { data: rawProjects = [] } = useProjects(orgId);
  const projects = rawProjects as any[];

  const [formData, setFormData] = useState({
    scheduleName: '',
    project: '',
    version: '',
    status: 'draft',
    notes: '',
    startDate: '',
    endDate: '',
    isBaseline: false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.scheduleName.trim()) {
      newErrors.scheduleName = 'Schedule name is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.version.trim()) {
      newErrors.version = 'Version is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Date validation
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await scheduleService.createProjectSchedule({
        organization_id: orgId,
        project_id: formData.project,
        schedule_name: formData.scheduleName,
        version: formData.version,
        status: formData.status,
        notes: formData.notes,
        start_date: formData.startDate,
        end_date: formData.endDate,
        is_baseline: formData.isBaseline
      });

      showSuccess({
        title: 'Schedule Created',
        message: 'Schedule has been created successfully'
      });

      setOpen(false);
      setFormData({
        scheduleName: '',
        project: '',
        version: '',
        status: 'draft',
        notes: '',
        startDate: '',
        endDate: '',
        isBaseline: false
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      showError({
        title: 'Creation Failed',
        message: 'Failed to create schedule. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Schedule
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Create New Schedule/Milestone
          </DialogTitle>
          <DialogDescription>
            Create a new project schedule or milestone container
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Schedule Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-name">Schedule Name *</Label>
                <Input
                  id="schedule-name"
                  placeholder="e.g., Q1 2024 Phase 1"
                  value={formData.scheduleName}
                  onChange={(e) => setFormData({...formData, scheduleName: e.target.value})}
                />
                {errors.scheduleName && <p className="text-sm text-destructive">{errors.scheduleName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  placeholder="e.g., v1.0"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                />
                {errors.version && <p className="text-sm text-destructive">{errors.version}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linked-project">Linked Project *</Label>
              <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
                {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
                {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="baseline">Baseline</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes or comments about this schedule..."
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-baseline"
                checked={formData.isBaseline}
                onChange={(e) => setFormData({...formData, isBaseline: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="is-baseline" className="cursor-pointer">Set as Baseline Schedule</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>Loading...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Schedule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// View Schedule Modal Component
export function ViewScheduleModal({ schedule, projects, trigger, open: controlledOpen, onOpenChange: controlledOnOpenChange }: {
  schedule: any;
  projects: any[];
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;
  const handleOpenChange = (val: boolean) => isControlled ? controlledOnOpenChange?.(val) : setInternalOpen(val);
  const projectName = projects.find(p => p.project_id === schedule.project_id)?.project_name || 'N/A';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Schedule Details
          </DialogTitle>
          <DialogDescription>
            View schedule information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Schedule Name</Label>
              <p className="font-medium">{schedule.schedule_name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Version</Label>
              <p className="font-medium">{schedule.version}</p>
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground">Project</Label>
            <p className="font-medium">{projectName}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Start Date</Label>
              <p className="font-medium">{schedule.start_date ? new Date(schedule.start_date).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">End Date</Label>
              <p className="font-medium">{schedule.end_date ? new Date(schedule.end_date).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground">Status</Label>
            <div className="mt-1">
              <Badge variant={
                schedule.status?.toLowerCase() === 'active' ? 'default' :
                schedule.status?.toLowerCase() === 'completed' ? 'default' :
                'outline'
              } className={
                schedule.status?.toLowerCase() === 'completed' ? 'bg-green-600' :
                schedule.status?.toLowerCase() === 'active' ? 'bg-blue-600' : ''
              }>
                {schedule.status}
              </Badge>
            </div>
          </div>

          {schedule.notes && (
            <div>
              <Label className="text-muted-foreground">Notes</Label>
              <p className="mt-1 text-sm whitespace-pre-wrap">{schedule.notes}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Baseline Schedule</Label>
              <p className="font-medium">{schedule.is_baseline ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Created</Label>
              <p className="text-sm">{schedule.created_at ? new Date(schedule.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Schedule Modal Component
export function EditScheduleModal({ schedule, projects, onSuccess, trigger, open: controlledOpen, onOpenChange: controlledOnOpenChange }: {
  schedule: any;
  projects: any[];
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;
  const handleOpenChange = (val: boolean) => isControlled ? controlledOnOpenChange?.(val) : setInternalOpen(val);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scheduleName: schedule.schedule_name || '',
    project: schedule.project_id || '',
    version: schedule.version || '',
    status: schedule.status || 'draft',
    notes: schedule.notes || '',
    startDate: schedule.start_date || '',
    endDate: schedule.end_date || '',
    isBaseline: schedule.is_baseline || false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.scheduleName.trim()) {
      newErrors.scheduleName = 'Schedule name is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.version.trim()) {
      newErrors.version = 'Version is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Date validation
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await scheduleService.updateSchedule(schedule.schedule_id, {
        schedule_name: formData.scheduleName,
        version: formData.version,
        status: formData.status,
        notes: formData.notes,
        start_date: formData.startDate,
        end_date: formData.endDate,
        is_baseline: formData.isBaseline
      } as any);

      showSuccess({
        title: 'Schedule Updated',
        message: 'Schedule has been updated successfully'
      });

      handleOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      showError({
        title: 'Update Failed',
        message: 'Failed to update schedule. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Schedule
          </DialogTitle>
          <DialogDescription>
            Update schedule information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-name">Schedule Name *</Label>
              <Input
                id="schedule-name"
                placeholder="e.g., Q1 2024 Phase 1"
                value={formData.scheduleName}
                onChange={(e) => setFormData({...formData, scheduleName: e.target.value})}
              />
              {errors.scheduleName && <p className="text-sm text-destructive">{errors.scheduleName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version *</Label>
              <Input
                id="version"
                placeholder="e.g., v1.0"
                value={formData.version}
                onChange={(e) => setFormData({...formData, version: e.target.value})}
              />
              {errors.version && <p className="text-sm text-destructive">{errors.version}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="linked-project">Linked Project *</Label>
            <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date *</Label>
              <Input
                id="start-date"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              />
              {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date *</Label>
              <Input
                id="end-date"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              />
              {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="baseline">Baseline</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional notes or comments about this schedule..."
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is-baseline"
              checked={formData.isBaseline}
              onChange={(e) => setFormData({...formData, isBaseline: e.target.checked})}
              className="h-4 w-4"
            />
            <Label htmlFor="is-baseline" className="cursor-pointer">Set as Baseline Schedule</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>Loading...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Schedule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
